import { useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import { Copied, Copy, Edit, Markdown as MarkdownIcon, Show, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { Modal, ModalClose, ModalContent, ModalTitle } from '@/components/modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { Toggle } from '@/components/toggle/Toggle'
import { SkillDownloadSurface, SkillOrigin } from '@/analytics/enums/skill.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSkillCopied, trackSkillDownloaded } from '@/analytics/utils/skill-analytics'
import type { PaxtonSkill, UserSkill } from '@/user-skills/schemas'
import { downloadSkillMarkdown } from '@/user-skills/utils/download-skill-markdown'
import SkillActionsMenu from './SkillActionsMenu'

enum SkillViewMode {
  PREVIEW = 'preview',
  RAW = 'raw',
}

/** Matches chat message copy feedback duration (Figma Skills copy/copied). */
const kCopiedFeedbackMs = 1500

type SkillViewerProps = {
  skill: UserSkill | PaxtonSkill
  origin: SkillOrigin
  onClose: () => void
  onEdit?: () => void
  onDelete?: () => void
}

export default function SkillViewer({ skill, origin, onClose, onEdit, onDelete }: SkillViewerProps) {
  const { trackEvent } = useAnalytics()
  const [viewMode, setViewMode] = useState(SkillViewMode.PREVIEW)
  const [copied, setCopied] = useState(false)
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current) clearTimeout(copiedResetTimeoutRef.current)
    }
  }, [])

  const showCopiedFeedback = () => {
    setCopied(true)
    if (copiedResetTimeoutRef.current) clearTimeout(copiedResetTimeoutRef.current)
    copiedResetTimeoutRef.current = setTimeout(() => {
      setCopied(false)
      copiedResetTimeoutRef.current = null
    }, kCopiedFeedbackMs)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(skill.description)
      trackSkillCopied(trackEvent, origin, origin === SkillOrigin.User ? skill.id : undefined)
      showCopiedFeedback()
    } catch {
      // Leave the default Copy label; avoid a false "Copied" state.
    }
  }

  const downloadSkill = () => {
    downloadSkillMarkdown(skill.name, skill.description)
    trackSkillDownloaded(trackEvent, origin, SkillDownloadSurface.Viewer, skill.id)
  }

  const copyAction = {
    label: copied ? 'Copied' : 'Copy to clipboard',
    icon: copied ? <Copied /> : <Copy />,
    onClick: () => void handleCopy(),
  }

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        width="xl"
        height="max-xl"
        className="h-[min(794px,calc(100dvh-38px))] max-h-[calc(100dvh-38px)] max-w-[860px]"
        bodyClassName="flex flex-col overflow-hidden"
        footer={
          <ModalFooterWithActions
            tertiaryAction={onEdit ? copyAction : undefined}
            secondaryAction={onEdit ? { label: 'Edit skill', icon: <Edit />, onClick: onEdit } : copyAction}
          />
        }
      >
        <div className="relative shrink-0 pb-4">
          <ModalTitle className="justify-center px-20 text-center text-base">{skill.name}</ModalTitle>
          <div className="absolute right-0 top-0 flex items-center gap-2.5">
            <SkillActionsMenu name={skill.name} onEdit={onEdit} onDownload={downloadSkill} onDelete={onDelete} />
            <ModalClose asChild>
              <IconButton aria-label="Close" color="neutral" icon={<XClose />} size="md" variant="plain" />
            </ModalClose>
          </div>
          <p className="mt-3 text-sm leading-5 text-brand-neutral-950">{skill.summary}</p>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-brand-neutral-400 bg-white">
          <div className="flex shrink-0 justify-end px-3 pb-3 pt-3 sm:absolute sm:right-2 sm:top-2.5 sm:z-10 sm:p-0">
            <Toggle
              aria-label="Instructions view"
              color="neutral"
              showLabels={false}
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: SkillViewMode.PREVIEW, label: 'Preview', icon: <Show /> },
                { value: SkillViewMode.RAW, label: 'Raw Markdown', icon: <MarkdownIcon /> },
              ]}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 sm:p-3 sm:pr-16">
            {viewMode === SkillViewMode.PREVIEW ? (
              <Markdown
                allowedElements={['p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'blockquote', 'ul', 'ol', 'li', 'pre', 'code']}
                unwrapDisallowed
                skipHtml
                className="break-words text-base leading-6 text-brand-neutral-950 [&_blockquote]:border-l-2 [&_blockquote]:border-brand-neutral-300 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-brand-neutral-100 [&_code]:px-1 [&_h1]:mb-3 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:font-bold [&_li]:ml-6 [&_ol]:list-decimal [&_p]:mb-4 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-brand-neutral-100 [&_pre]:p-3 [&_ul]:list-disc"
              >
                {skill.description}
              </Markdown>
            ) : (
              <pre className="whitespace-pre-wrap break-words font-mono text-sm leading-5 text-brand-neutral-950">{skill.description}</pre>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
